import React from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../../components/ui/Card";
import { Badge } from "../../../components/ui/Badge";
import { CheckCircle, XCircle, HelpCircle, Minus } from "lucide-react";
import { FeatureCategory } from "../types";

interface ComparisonTableProps {
    featureCategories: FeatureCategory[];
}

const ComparisonTable: React.FC<ComparisonTableProps> = ({
    featureCategories,
}) => {
    // Function to render the appropriate cell content based on the value
    const renderCellContent = (
        value: boolean | string | number,
        type: string
    ) => {
        if (typeof value === "boolean") {
            if (value === true) {
                return (
                    <div className="flex justify-center">
                        <CheckCircle
                            className={`h-5 w-5 ${
                                type === "basic"
                                    ? "text-gray-500"
                                    : type === "plus"
                                    ? "text-blue-500"
                                    : type === "pro"
                                    ? "text-purple-500"
                                    : "text-gray-400"
                            }`}
                        />
                    </div>
                );
            } else {
                return (
                    <div className="flex justify-center">
                        <XCircle className="h-5 w-5 text-gray-300" />
                    </div>
                );
            }
        } else if (typeof value === "string" || typeof value === "number") {
            return (
                <div className="text-center font-medium text-sm">{value}</div>
            );
        }

        return (
            <div className="flex justify-center">
                <Minus className="h-5 w-5 text-gray-300" />
            </div>
        );
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-gray-200">
                            <th className="p-4 text-left font-semibold text-gray-900 bg-gray-50">
                                Features
                            </th>
                            <th className="p-4 text-center bg-gray-50">
                                <div className="text-gray-900 font-semibold">Basic</div>
                                <div className="text-gray-500 text-xs font-normal mt-1">Free</div>
                            </th>
                            <th className="p-4 text-center bg-blue-50">
                                <div className="text-blue-900 font-semibold">Plus</div>
                                <div className="text-blue-600 text-xs font-normal mt-1">$34.99/mo</div>
                            </th>
                            <th className="p-4 text-center bg-purple-50">
                                <div className="text-purple-900 font-semibold">Pro</div>
                                <div className="text-purple-600 text-xs font-normal mt-1">$199.99/mo</div>
                            </th>
                            <th className="p-4 text-center bg-gray-50">
                                <div className="text-gray-700 font-semibold">Competitors</div>
                                <div className="text-gray-500 text-xs font-normal mt-1">$300+/mo</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {featureCategories.map(
                            (category, categoryIndex) => (
                                <React.Fragment key={categoryIndex}>
                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="px-4 py-3 bg-gray-50 font-semibold text-gray-700 text-sm uppercase tracking-wider border-y border-gray-200">
                                            {category.name}
                                        </td>
                                    </tr>
                                        {category.features.map(
                                            (feature, featureIndex) => (
                                                <tr
                                                    key={`${categoryIndex}-${featureIndex}`}
                                                    className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                                                    <td className="p-4">
                                                        <div className="flex items-start gap-2">
                                                            <div>
                                                                <p className="font-medium text-gray-900 text-sm">
                                                                    {
                                                                        feature.name
                                                                    }
                                                                </p>
                                                                {feature.description && (
                                                                    <p className="text-xs text-gray-500 mt-1">
                                                                        {
                                                                            feature.description
                                                                        }
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="p-4 text-center bg-white">
                                                        {renderCellContent(
                                                            feature.basic,
                                                            "basic"
                                                        )}
                                                    </td>
                                                    <td className="p-4 text-center bg-blue-50/30">
                                                        {renderCellContent(
                                                            feature.plus,
                                                            "plus"
                                                        )}
                                                    </td>
                                                    <td className="p-4 text-center bg-purple-50/30">
                                                        {renderCellContent(
                                                            feature.pro,
                                                            "pro"
                                                        )}
                                                    </td>
                                                    <td className="p-4 border-b text-center">
                                                        {renderCellContent(
                                                            feature.competitors,
                                                            "competitors"
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </React.Fragment>
                                )
                            )}
                        </tbody>
                    </table>
            </div>
        </div>
    );
};

export default ComparisonTable;
